import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService from '../../services/api';

interface CampaignCode {
  id: number;
  code: string;
  discount_percentage: number;
  description?: string;
  is_active: boolean;
  usage_limit?: number;
  times_used: number;
  valid_from?: string;
  valid_until?: string;
  created_at: string;
  updated_at: string;
}

interface CampaignCodeStats {
  total_codes: number;
  active_codes: number;
  total_usage: number;
}

interface NewCampaignCode {
  code: string;
  discount_percentage: number;
  description?: string;
  is_active: boolean;
  usage_limit?: number;
  valid_from?: string;
  valid_until?: string;
}

const CampaignCodesContainer = styled.div`
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
`;

const Title = styled.h2`
  color: var(--color-secondary);
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &.primary {
    background: var(--color-accent);
    color: white;
    
    &:hover {
      background: #0056b3;
    }
  }
  
  &.secondary {
    background: var(--color-primary);
    color: white;
    
    &:hover {
      background: #6c757d;
    }
  }
  
  &.danger {
    background: #dc3545;
    color: white;
    
    &:hover {
      background: #c82333;
    }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
`;

const StatCard = styled.div`
  background: white;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  text-align: center;
  
  .value {
    font-size: 2rem;
    font-weight: bold;
    color: var(--color-accent);
    margin-bottom: 8px;
  }
  
  .label {
    color: var(--color-text-light);
    font-size: 0.9rem;
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 30px;
  border-radius: 8px;
  border: 1px solid #e9ecef;
  margin-bottom: 30px;
`;

const FormTitle = styled.h3`
  color: var(--color-secondary);
  margin-bottom: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  .label {
    display: block;
    font-weight: 500;
    color: var(--color-secondary);
    margin-bottom: 6px;
  }
  
  input, textarea, select {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
    
    &:focus {
      outline: none;
      border-color: var(--color-accent);
      box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
    }
  }
  
  input[type="checkbox"] {
    width: auto;
    margin-right: 8px;
  }
`;

const CodesTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e9ecef;
  
  th {
    background: var(--color-primary);
    color: white;
    padding: 15px;
    text-align: left;
    font-weight: 500;
  }
  
  td {
    padding: 15px;
    border-bottom: 1px solid #e9ecef;
  }
  
  tr:last-child td {
    border-bottom: none;
  }
  
  tr:nth-child(even) {
    background-color: #f8f9fa;
  }
`;

const StatusBadge = styled.span<{ $active: boolean }>`
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  background-color: ${props => props.$active ? '#d4edda' : '#f8d7da'};
  color: ${props => props.$active ? '#155724' : '#721c24'};
`;

const CodeBadge = styled.code`
  background-color: #f8f9fa;
  padding: 4px 8px;
  border-radius: 4px;
  font-family: 'Courier New', monospace;
  font-weight: bold;
  color: var(--color-accent);
`;

const AdminCampaignCodes: React.FC = () => {
  const [codes, setCodes] = useState<CampaignCode[]>([]);
  const [stats, setStats] = useState<CampaignCodeStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState<CampaignCode | null>(null);
  const [formData, setFormData] = useState<NewCampaignCode>({
    code: '',
    discount_percentage: 10,
    description: '',
    is_active: true,
    usage_limit: undefined,
    valid_from: '',
    valid_until: ''
  });

  const fetchCodes = async () => {
    try {
      const response = await apiService.get('/campaign-codes');
      setCodes(response);
    } catch (err: any) {
      console.error('Campaign codes fetch error:', err);
      setError(`Kunde inte ladda kampanjkoder: ${err.message || err.toString()}`);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.get('/campaign-codes/statistics');
      setStats(response);
    } catch (err: any) {
      console.error('Failed to fetch campaign code stats:', err);
      setError(`Kunde inte ladda statistik: ${err.message || err.toString()}`);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCodes(), fetchStats()]);
      setLoading(false);
    };
    
    loadData();
  }, []);

  const handleCreateCode = async () => {
    try {
      setError('');
      
      if (!formData.code.trim()) {
        setError('Kampanjkod krävs');
        return;
      }
      
      if (formData.discount_percentage <= 0 || formData.discount_percentage > 100) {
        setError('Rabattprocent måste vara mellan 1 och 100');
        return;
      }
      
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        usage_limit: formData.usage_limit || null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };
      
      await apiService.post('/campaign-codes', payload);
      await fetchCodes();
      await fetchStats();
      
      setShowForm(false);
      setFormData({
        code: '',
        discount_percentage: 10,
        description: '',
        is_active: true,
        usage_limit: undefined,
        valid_from: '',
        valid_until: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunde inte skapa kampanjkod');
    }
  };

  const handleUpdateCode = async () => {
    if (!editingCode) return;
    
    try {
      setError('');
      
      if (!formData.code.trim()) {
        setError('Kampanjkod krävs');
        return;
      }
      
      if (formData.discount_percentage <= 0 || formData.discount_percentage > 100) {
        setError('Rabattprocent måste vara mellan 1 och 100');
        return;
      }
      
      const payload = {
        ...formData,
        code: formData.code.toUpperCase(),
        usage_limit: formData.usage_limit || null,
        valid_from: formData.valid_from || null,
        valid_until: formData.valid_until || null
      };
      
      await apiService.put(`/campaign-codes/${editingCode.id}`, payload);
      await fetchCodes();
      await fetchStats();
      
      setEditingCode(null);
      setShowForm(false);
      setFormData({
        code: '',
        discount_percentage: 10,
        description: '',
        is_active: true,
        usage_limit: undefined,
        valid_from: '',
        valid_until: ''
      });
    } catch (err: any) {
      setError(err.response?.data?.message || 'Kunde inte uppdatera kampanjkod');
    }
  };

  const handleDeleteCode = async (id: number) => {
    if (!window.confirm('Är du säker på att du vill radera denna kampanjkod?')) {
      return;
    }
    
    try {
      await apiService.delete(`/campaign-codes/${id}`);
      await fetchCodes();
      await fetchStats();
    } catch (err) {
      setError('Kunde inte radera kampanjkod');
    }
  };

  const startEdit = (code: CampaignCode) => {
    setEditingCode(code);
    setFormData({
      code: code.code,
      discount_percentage: code.discount_percentage,
      description: code.description || '',
      is_active: code.is_active,
      usage_limit: code.usage_limit || undefined,
      valid_from: code.valid_from ? code.valid_from.substring(0, 16) : '',
      valid_until: code.valid_until ? code.valid_until.substring(0, 16) : ''
    });
    setShowForm(true);
  };

  const cancelForm = () => {
    setEditingCode(null);
    setShowForm(false);
    setError('');
    setFormData({
      code: '',
      discount_percentage: 10,
      description: '',
      is_active: true,
      usage_limit: undefined,
      valid_from: '',
      valid_until: ''
    });
  };

  if (loading) {
    return <CampaignCodesContainer>Laddar kampanjkoder...</CampaignCodesContainer>;
  }

  return (
    <CampaignCodesContainer>
      <Header>
        <Title>Kampanjkoder</Title>
        <ActionButtons>
          <Button 
            className="primary" 
            onClick={() => setShowForm(!showForm)}
          >
            {showForm ? 'Avbryt' : 'Skapa ny kampanjkod'}
          </Button>
        </ActionButtons>
      </Header>

      {stats && (
        <StatsGrid>
          <StatCard>
            <div className="value">{stats.total_codes}</div>
            <div className="label">Totalt antal koder</div>
          </StatCard>
          <StatCard>
            <div className="value">{stats.active_codes}</div>
            <div className="label">Aktiva koder</div>
          </StatCard>
          <StatCard>
            <div className="value">{stats.total_usage}</div>
            <div className="label">Totalt antal användningar</div>
          </StatCard>
        </StatsGrid>
      )}

      {error && (
        <div style={{ 
          background: '#f8d7da', 
          color: '#721c24', 
          padding: '12px', 
          borderRadius: '4px', 
          marginBottom: '20px' 
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <FormContainer>
          <FormTitle>
            {editingCode ? 'Redigera kampanjkod' : 'Skapa ny kampanjkod'}
          </FormTitle>
          
          <FormGrid>
            <FormGroup>
              <label className="label">Kod *</label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                placeholder="t.ex. SUMMER2024"
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">Rabattprocent * (%)</label>
              <input
                type="number"
                min="1"
                max="100"
                value={formData.discount_percentage}
                onChange={(e) => setFormData({ ...formData, discount_percentage: parseFloat(e.target.value) })}
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">Beskrivning</label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Beskrivning av kampanjen"
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">Användningsgräns</label>
              <input
                type="number"
                min="1"
                value={formData.usage_limit || ''}
                onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value ? parseInt(e.target.value) : undefined })}
                placeholder="Lämna tom för obegränsad"
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">Giltig från</label>
              <input
                type="datetime-local"
                value={formData.valid_from}
                onChange={(e) => setFormData({ ...formData, valid_from: e.target.value })}
              />
            </FormGroup>
            
            <FormGroup>
              <label className="label">Giltig till</label>
              <input
                type="datetime-local"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
              />
            </FormGroup>
          </FormGrid>
          
          <FormGroup>
            <label>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              />
              Aktiv
            </label>
          </FormGroup>
          
          <ActionButtons>
            <Button 
              className="primary"
              onClick={editingCode ? handleUpdateCode : handleCreateCode}
            >
              {editingCode ? 'Uppdatera' : 'Skapa'}
            </Button>
            <Button className="secondary" onClick={cancelForm}>
              Avbryt
            </Button>
          </ActionButtons>
        </FormContainer>
      )}

      <CodesTable>
        <thead>
          <tr>
            <th>Kod</th>
            <th>Rabatt</th>
            <th>Beskrivning</th>
            <th>Status</th>
            <th>Användning</th>
            <th>Giltighet</th>
            <th>Åtgärder</th>
          </tr>
        </thead>
        <tbody>
          {codes.map((code) => (
            <tr key={code.id}>
              <td>
                <CodeBadge>{code.code}</CodeBadge>
              </td>
              <td>{code.discount_percentage}%</td>
              <td>{code.description || '-'}</td>
              <td>
                <StatusBadge $active={code.is_active}>
                  {code.is_active ? 'Aktiv' : 'Inaktiv'}
                </StatusBadge>
              </td>
              <td>
                {code.times_used}{code.usage_limit ? ` / ${code.usage_limit}` : ''}
              </td>
              <td>
                {code.valid_from && code.valid_until ? (
                  <>
                    {new Date(code.valid_from).toLocaleDateString()} - {new Date(code.valid_until).toLocaleDateString()}
                  </>
                ) : code.valid_from ? (
                  `Från ${new Date(code.valid_from).toLocaleDateString()}`
                ) : code.valid_until ? (
                  `Till ${new Date(code.valid_until).toLocaleDateString()}`
                ) : (
                  'Obegränsad'
                )}
              </td>
              <td>
                <ActionButtons>
                  <Button 
                    className="secondary"
                    onClick={() => startEdit(code)}
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                  >
                    Redigera
                  </Button>
                  <Button 
                    className="danger"
                    onClick={() => handleDeleteCode(code.id)}
                    style={{ padding: '5px 10px', fontSize: '12px' }}
                  >
                    Radera
                  </Button>
                </ActionButtons>
              </td>
            </tr>
          ))}
        </tbody>
      </CodesTable>

      {codes.length === 0 && (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px', 
          color: '#6c757d' 
        }}>
          Inga kampanjkoder har skapats ännu.
        </div>
      )}
    </CampaignCodesContainer>
  );
};

export default AdminCampaignCodes;